import React, { Component } from "react";
import { toastService } from "../../../services/toastService";
import { PageLayout } from "../../../components/ui";
import { Col, FormGroup, Input, Label, Row } from "reactstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { merchantVendorApiService } from "../../../services/api/merchants/MerchantVendorApiService";
import { MerchantVendorCredentialModel } from "../../../models/MerchantVendor";
import { Result } from "../../../models/Result";
import { DataTable, DataTableFilterMeta } from "primereact/datatable";
import { FilterMatchMode } from "primereact/api";
import ExtendedInputText from "../../../components/ExtendedInputText";
import { SortOrderType } from "../../../models/SearchRequestModel";
import { paginatorTemplate } from "../../Paginator";
import { Column } from "primereact/column";
import { InputTextFilerTemplate } from "../../Operations/TransactionFilterTemplates";
import { FilterX } from "lucide-react";

interface VendorSettingCredentialsProps {
    vendorId: number;
    editable: boolean;
}

interface VendorSettingCredentialsState {
    isLoading: boolean;
    activeTabIndex: number;
    vendorCredentials: MerchantVendorCredentialModel[];
    filters: DataTableFilterMeta | undefined;
    totalRecords: number;
    rows: number;
    editRowId: number | null;
    backupRowData: MerchantVendorCredentialModel | null;
}

export default class VendorSettingCredentials extends Component<VendorSettingCredentialsProps, VendorSettingCredentialsState> {
    initializeFilters = {
        name: { value: null, matchMode: FilterMatchMode.CONTAINS },
        description: { value: null, matchMode: FilterMatchMode.CONTAINS },
        nameBG: { value: null, matchMode: FilterMatchMode.CONTAINS },
        nameEN: { value: null, matchMode: FilterMatchMode.CONTAINS },
        placeHolderBG: { value: null, matchMode: FilterMatchMode.CONTAINS },
        placeHolderEN: { value: null, matchMode: FilterMatchMode.CONTAINS },
        hintBG: { value: null, matchMode: FilterMatchMode.CONTAINS },
        hintEN: { value: null, matchMode: FilterMatchMode.CONTAINS },
    };
    constructor(props: VendorSettingCredentialsProps | Readonly<VendorSettingCredentialsProps>) {
        super(props);

        this.state = {
            isLoading: true,
            activeTabIndex: 0,
            vendorCredentials: [],
            filters: this.initializeFilters,
            totalRecords: 0,
            rows: 10,
            editRowId: null,
            backupRowData: null,
        };
    }

    async componentDidMount() {
        await this.loadVendorCredentialsData();
    }

    loadVendorCredentialsData = async () => {
        await this.loadVendorCredentials();
        this.setState({ isLoading: false });
    };

    loadVendorCredentials = async () => {
        const vendorCredentials = await merchantVendorApiService.getVendorCredentials(this.props.vendorId);
        if (!vendorCredentials.success) {
            toastService.error(vendorCredentials.message);
            this.setState({ isLoading: false });
            return;
        }
        this.setState({ vendorCredentials: vendorCredentials.data, totalRecords: vendorCredentials.data.length });
    };

    saveRow = async (model: MerchantVendorCredentialModel) => {
        this.setState({ isLoading: true });
        const request = {
            id: model.id,
            nameBG: model.nameBG,
            nameEN: model.nameEN,
            placeHolderBG: model.placeHolderBG,
            placeHolderEN: model.placeHolderEN,
            hintBG: model.placeHolderBG,
            hintEN: model.hintEN,
        } as MerchantVendorCredentialModel;

        const result = await merchantVendorApiService.updateVendorCredentials(request);
        if (!result.success) {
            toastService.error(result.message);
            this.setState({ isLoading: false });
            return;
        }
        toastService.success(result.message);
        this.setState({ editRowId: null, backupRowData: null });
        await this.loadVendorCredentialsData();
    };

    editRow = (rowData: MerchantVendorCredentialModel) => {
        this.setState({
            editRowId: rowData.id,
            backupRowData: { ...rowData },
        });
    };

    cancelEdit = () => {
        const { editRowId, backupRowData, vendorCredentials } = this.state;

        if (editRowId && backupRowData) {
            this.setState({
                vendorCredentials: vendorCredentials.map((credential) => (credential.id === editRowId ? backupRowData : credential)),
                editRowId: null,
                backupRowData: null,
            });
        }
    };

    renderEditableField = (field: keyof MerchantVendorCredentialModel, rowData: MerchantVendorCredentialModel) => {
        const value = rowData[field] as string;
        return this.state.editRowId === rowData.id ? (
            <ExtendedInputText type="text" name={field} value={value || ""} onChange={(value) => this.onInputChange(field, value, rowData)} />
        ) : (
            rowData[field]
        );
    };

    onInputChange = (fieldName: string, value: string, rowData: MerchantVendorCredentialModel) => {
        const updatedVendorServices = this.state.vendorCredentials!.map((vendor) => {
            if (vendor.id === rowData.id) {
                return { ...vendor, [fieldName]: value };
            }
            return vendor;
        });
        this.setState({ vendorCredentials: updatedVendorServices });
    };

    renderActionButtons = (rowData: MerchantVendorCredentialModel) => {
        if (rowData.isDeleted) {
            return null;
        }

        if (this.state.editRowId === rowData.id) {
            return (
                <>
                    <Button text onClick={() => this.saveRow(rowData)} label="Save" />
                    <Button text onClick={this.cancelEdit} label="Cancel" />
                </>
            );
        }

        return (
            <Button text onClick={() => this.editRow(rowData)} label="Edit" />
        );
    };

    renderHeader = () => (
        <span className="p-input-icon-left" style={{ display: "flex", justifyContent: "space-between" }}>
            <Button
                type="button"
                icon={<FilterX size={16} />}
                label="Clear"
                outlined
                onClick={() => {
                    this.setState({ filters: this.initializeFilters });
                }}
            />
        </span>
    );

    render() {
        return (
            <PageLayout noTitle>
                <DataTable
                                    header={this.renderHeader}
                                    editMode="row"
                                    dataKey="id"
                                    sortField="id"
                                    sortOrder={SortOrderType.Ascending}
                                    paginator
                                    paginatorTemplate={paginatorTemplate}
                                    rows={this.state.rows}
                                    totalRecords={this.state.totalRecords}
                                    value={this.state.vendorCredentials}
                                    loading={this.state.isLoading}
                                    stripedRows={true}
                                    removableSort
                                    rowClassName={(rowData) => (rowData.isDeleted ? "table-secondary" : "")}
                                    filters={this.state.filters}
                                    onFilter={(e) => this.setState({ filters: e.filters })}
                                    filterDisplay="menu"
                                >
                                    <Column
                                        field="name"
                                        header="Name"
                                        sortable
                                        filter
                                        filterField="name"
                                        filterMatchMode="contains"
                                        showFilterMatchModes={false}
                                        filterElement={InputTextFilerTemplate}
                                    />

                                    <Column
                                        field="description"
                                        header="Description"
                                        sortable
                                        filter
                                        filterField="description"
                                        filterMatchMode="contains"
                                        showFilterMatchModes={false}
                                        filterElement={InputTextFilerTemplate}
                                    />

                                    <Column
                                        field="nameBG"
                                        header="Name BG"
                                        body={(rowData) => this.renderEditableField("nameBG", rowData)}
                                        sortable
                                        filter
                                        filterField="nameBG"
                                        filterMatchMode="contains"
                                        showFilterMatchModes={false}
                                        filterElement={InputTextFilerTemplate}
                                    />
                                    <Column
                                        field="nameEN"
                                        header="Name EN"
                                        body={(rowData) => this.renderEditableField("nameEN", rowData)}
                                        sortable
                                        filter
                                        filterField="nameEN"
                                        filterMatchMode="contains"
                                        showFilterMatchModes={false}
                                        filterElement={InputTextFilerTemplate}
                                    />

                                    <Column
                                        field="placeHolderBG"
                                        header="PlaceHolder BG"
                                        body={(rowData) => this.renderEditableField("placeHolderBG", rowData)}
                                        sortable
                                        filter
                                        filterField="placeHolderBG"
                                        filterMatchMode="contains"
                                        showFilterMatchModes={false}
                                        filterElement={InputTextFilerTemplate}
                                    />
                                    <Column
                                        field="placeHolderEN"
                                        header="PlaceHolder EN"
                                        body={(rowData) => this.renderEditableField("placeHolderEN", rowData)}
                                        sortable
                                        filter
                                        filterField="placeHolderEN"
                                        filterMatchMode="contains"
                                        showFilterMatchModes={false}
                                        filterElement={InputTextFilerTemplate}
                                    />
                                    <Column
                                        field="hintBG"
                                        header="Hint BG"
                                        body={(rowData) => this.renderEditableField("hintBG", rowData)}
                                        sortable
                                        filter
                                        filterField="hintBG"
                                        filterMatchMode="contains"
                                        showFilterMatchModes={false}
                                        filterElement={InputTextFilerTemplate}
                                    />
                                    <Column
                                        field="hintEN"
                                        header="Hint EN"
                                        body={(rowData) => this.renderEditableField("hintEN", rowData)}
                                        sortable
                                        filter
                                        filterField="hintEN"
                                        filterMatchMode="contains"
                                        showFilterMatchModes={false}
                                        filterElement={InputTextFilerTemplate}
                                    />
                    <Column header="Actions" body={(rowData) => this.renderActionButtons(rowData)} />
                </DataTable>
            </PageLayout>
        );
    }
}
